import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { fetchPlacedToday } from '../services/api';

const PAGE_SIZE = 100;

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtMoney(v) {
  return `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

const todayIso = new Date().toISOString().split('T')[0];

export default function PlacedToday({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(todayIso);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchPlacedToday({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const rows = Array.isArray(data) ? data : data?.data ?? data?.records?.data ?? [];
  const total = data?.totalRows ?? data?.total ?? rows.length;
  const newOrders = rows.filter(r => !r.originalOrderId);
  const reships = rows.filter(r => !!r.originalOrderId);
  const totalRev = rows.reduce((s, r) => s + Number(r.orderTotal ?? 0), 0);
  const newRev = newOrders.reduce((s, r) => s + Number(r.orderTotal ?? 0), 0);

  const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 11.5, borderBottom: '1px solid var(--gray-100)' };

  const renderRow = (row, i, isReship) => (
    <tr key={row.orderId + '-' + i} style={{ background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
      <td style={{ ...td, fontWeight: 600 }}>
        <a
          href="#"
          onClick={e => { e.preventDefault(); navigateTo('order-details', row.bcOrderId ?? row.orderId); }}
          style={{ color: 'var(--orange)', textDecoration: 'none' }}
        >
          {row.bcOrderId ?? row.orderId ?? '—'}
        </a>
        {isReship && (
          <span style={{ fontSize: 9, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '1px 5px', borderRadius: 3, marginLeft: 6, verticalAlign: 'middle' }}>RESHIP</span>
        )}
      </td>
      <td style={{ ...td, color: 'var(--navy)' }}>{row.billTo ?? '—'}</td>
      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(row.orderTotal)}</td>
      <td style={{ ...td, color: 'var(--gray-700)' }}>{row.shipType ?? '—'}</td>
      <td style={{ ...td, color: 'var(--gray-600)' }}>{fmtDate(row.deliveryDate)}</td>
    </tr>
  );

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Orders Placed Today</h2>
        <button onClick={() => { setPage(1); load(); }} className="btn btn-ghost" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: '10px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={start} onChange={e => setStart(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'New Orders', value: newOrders.length.toLocaleString() },
            { label: 'Reships', value: reships.length.toLocaleString() },
            { label: 'Revenue (New)', value: fmtMoney(newRev) },
            { label: 'AOV', value: newOrders.length > 0 ? fmtMoney(newRev / newOrders.length) : '$0.00' },
          ].map(({ label, value }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Orders {total > 0 && `(${total})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders found.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={th}>Order #</th>
                    <th style={th}>Customer</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th>
                    <th style={th}>Ship Method</th>
                    <th style={th}>Sched. Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => renderRow(row, i, !!row.originalOrderId))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center', color: 'var(--gray-500)' }}>Page {page}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
